import { Ctx } from '@app/context'
import { GT_Time } from '../../types/times'
import { Date_Filter } from '@app/filter/date-filter'
import { DateTime } from 'luxon'
import ClickEvent = JQuery.ClickEvent
import { minutes_to_duration } from '@app/utils/date'
import { Evaluation_Chart } from '@app/vis/evaluation-chart'
import { build_month_buckets, build_week_buckets, build_year_buckets, build_year_week_buckets, Chart_Item } from '@app/vis/chart-buckets'
import { GT_User } from '../../types/globals'
import { Goehler_Tool_REST_Client } from '@app/utils/rest-client'
import { handle_api_error } from '@app/utils/api-error'

export type Time_Date_Mode = 'week' | 'month' | 'year'
export type Year_Chart_Granularity = 'months' | 'weeks'

export class Time_List {
    date_mode: Time_Date_Mode = 'week'
    year_chart_granularity: Year_Chart_Granularity = 'months'

    /** IDs der im Mitarbeiter-Filter ausgewählten Nutzer. */
    selected_user_ids: number[] = []

    rest_client: Goehler_Tool_REST_Client

    date_filter: Date_Filter
    container: HTMLElement|null = null
    chart: Evaluation_Chart|null = null
    protected is_ready = false

    constructor( ctx: Ctx ) {
        this.rest_client = new Goehler_Tool_REST_Client()
        this.set_times( ctx.times )
        this.set_container()
        this.set_chart()
        this.render_user_filter()

        this.date_filter = new Date_Filter()

        this.render_results()
    }

    ready() {
        this.init_actions()
        this.init_user_filter()

        this.is_ready = true
        this.get_container().dataset.mode = this.date_mode
        this.render_week_chart()
    }

    init_actions() {
        jQuery( '[data-action="update-times-view"]' ).on( 'click', ( e: ClickEvent ) => this.update_view_mode( e.target ) )
        jQuery( '.list-date-container button' ).on( 'click', ( e: ClickEvent ) => this.change_current_date( e ) )
        jQuery( '#year-chart-granularity button' ).on( 'click', ( e: ClickEvent ) => this.update_year_chart_granularity( e.currentTarget ) )
    }

    public set_times( posts: GT_Time[] = [] ) {
        gt_results.times = posts
    }

    /**
     * Liefert die Arbeitszeiten des geladenen Zeitraums, gefiltert nach der
     * Mitarbeiterauswahl. Ohne Auswahl werden alle Zeiten zurückgegeben.
     *
     * @returns Gefilterte Arbeitszeiten.
     */
    public get_times(): GT_Time[] {
        if ( this.selected_user_ids.length === 0 ) {
            return gt_results.times
        }

        return gt_results.times.filter(
            ( time: GT_Time ): boolean => {
                const id = time.start_user?.id

                return id != null && this.selected_user_ids.includes( id )
            }
        )
    }

    /**
     * Liefert die im Filter angebotenen Mitarbeiter, alphabetisch sortiert.
     *
     * @returns Mitarbeiter mit ID und Anzeigename.
     */
    protected get_filter_users(): GT_User[] {
        const workers: GT_User[] = gt_workers ?? []

        return [ ...workers ].sort( ( a: GT_User, b: GT_User ) => a.name.localeCompare( b.name, 'de' ) )
    }

    /**
     * Rendert die Auswahlliste des Mitarbeiter-Filters.
     */
    protected render_user_filter() {
        const menu = this.get_container().querySelector( '#list-times-user-filter .menu' )

        if ( ! menu ) {
            return
        }

        menu.innerHTML = this.get_filter_users()
            .map( ( user: GT_User ) => `<div class="item" data-value="${user.id}">${user.name}</div>` )
            .join( '' )
    }

    /**
     * Initialisiert das Fomantic-Dropdown des Mitarbeiter-Filters.
     */
    protected init_user_filter() {
        jQuery( '#list-times-user-filter' ).dropdown( {
            onChange: ( value: string ) => this.update_user_filter( value )
        } )
    }

    /**
     * Übernimmt die Auswahl des Mitarbeiter-Filters und rendert neu.
     *
     * @param value Kommaseparierte User-IDs aus dem Fomantic-Dropdown.
     */
    update_user_filter( value: string ) {
        this.selected_user_ids = ( value ?? '' )
            .split( ',' )
            .map( ( id: string ) => parseInt( id, 10 ) )
            .filter( ( id: number ) => ! isNaN( id ) )

        this.render_results()
    }

    protected set_container() {
        const element: HTMLElement|null = document.querySelector( '.tab[data-tab="evaluation-times"]' )

        if ( ! element ) {
            throw new Error( 'no container found' );
        }

        this.container = element
    }

    protected get_container() {
        if ( ! this.container ) {
            throw new Error( 'no container found' );
        }
        return this.container;
    }

    protected set_chart() {
        this.chart = new Evaluation_Chart( {
            selector: '#chart',
            format_value: ( value: number ) => minutes_to_duration( value ),
            y_label: 'Stunden'
        } )
    }

    protected get_chart() {
        if ( ! this.chart ) {
            throw new Error( 'no chart found' );
        }
        return this.chart;
    }

    protected get_chart_items(): Chart_Item[] {
        return this.get_times()
            .map( ( time: GT_Time ) => ( {
                date: DateTime.fromSQL( time.start_date ?? '' ),
                value: time.duration
            } ) )
            .filter( ( item: Chart_Item ) => item.date.isValid )
    }

    render_week_chart() {
        if ( ! this.is_ready ) {
            return
        }

        const week = this.date_filter.current_week
        const days = [ week?.days?.mon, week?.days?.tue, week?.days?.wed, week?.days?.thu, week?.days?.fri ]
            .filter( ( day ): day is DateTime => day !== undefined )

        this.get_chart().render(
            build_week_buckets( days, this.get_chart_items() ),

            // temporär entfernt.
            // { target: 8 * 60 * 4, target_label: 'Soll' }
        )
    }

    render_month_chart() {
        if ( ! this.is_ready ) {
            return
        }

        this.get_chart().render(
            build_month_buckets(
                this.date_filter.current_month.start_of_month,
                this.date_filter.current_month.end_of_month,
                this.get_chart_items()
            ),

            // temporär entfernt.
            // { target: 40 * 60 * 4, target_label: 'Soll' }
        )
    }

    render_year_chart() {
        if ( ! this.is_ready ) {
            return
        }

        const year = this.date_filter.current_year.year

        if ( this.year_chart_granularity === 'weeks' ) {
            this.get_chart().render(
                build_year_week_buckets( year, this.get_chart_items() ),

                // temporär entfernt.
                // { target: 40 * 60 * 4, target_label: 'Soll' }
            )
        } else {
            this.get_chart().render(
                build_year_buckets( year, this.get_chart_items() )
            )
        }
    }

    update_year_chart_granularity( target: HTMLElement ) {
        const granularity = target.dataset.granularity as Year_Chart_Granularity

        if ( ! granularity ) {
            return
        }

        const button_group = target.parentElement

        if ( ! button_group ) {
            throw new Error( 'no buttons' );
        }

        const buttons = button_group.querySelectorAll( 'button' )

        for ( const button of Array.from( buttons ) ) {
            button.classList.remove( 'active' )
        }

        target.classList.add( 'active' )

        this.year_chart_granularity = granularity
        this.render_year_chart()
    }

    update_view_mode( target: HTMLElement ) {
        console.log( target, target.dataset.view )
        const mode = target.dataset.view as Time_Date_Mode

        if ( ! mode || ! target ) {
            return
        }

        this.clear_results()

        const button_group = target.parentElement

        if ( ! button_group ) {
            throw new Error( 'no buttons' );
        }

        const buttons = button_group.querySelectorAll( 'button' )

        for ( const button of Array.from( buttons ) ) {
            button.classList.remove( 'active' )
        }

        target.classList.add( 'active' )

        this.set_mode( mode )
    }

    set_mode( mode: Time_Date_Mode ) {
        console.log( 'set mode', mode, this.get_container() )
        this.date_mode = mode

        this.get_container().dataset.mode = mode

        const now = DateTime.local()

        switch ( mode ) {
            case 'week':
                this.date_filter.update_current_week( now )
                break
            case 'month':
                this.date_filter.update_current_month( now )
                break
            case 'year':
                this.date_filter.update_current_year( now )
                break
        }

        const start = this.date_filter.get_current_start()
        const end  = this.date_filter.get_current_end()

        void this.api_load_times( start, end )
    }


    render_results(): void {
        console.log( 'render times' )

        console.log( 'chart items', this.get_chart_items() )

        const sum: number = this.calculate_sum()
        this.render_widget_value( 'times-sum', `${minutes_to_duration( sum )} Stunden` )

        const workers_count = new Set(
            this.get_times()
                .map( ( item: GT_Time ) => item.start_user?.id )
                .filter( ( id: number ) => id != null )
        ).size

        this.render_widget_value( 'times-workers', workers_count.toString() )

        const project_ids = new Set(
            this.get_times().map( ( time: GT_Time ) => time.project_post_id )
        )

        this.render_widget_value( 'times-projects', project_ids.size.toString() )

        switch ( this.date_mode ) {
            case 'week':
                this.render_week_data()
                this.render_week_chart()
                break
            case 'month':
                this.render_month_data()
                this.render_month_chart()
                break
            case 'year':
                this.render_year_data()
                this.render_year_chart()
                break
        }
    }

    render_week_data() {
        this.render_week_day( 'mon', this.date_filter.current_week?.days?.mon )
        this.render_week_day( 'tue', this.date_filter.current_week?.days?.tue )
        this.render_week_day( 'wed', this.date_filter.current_week?.days?.wed )
        this.render_week_day( 'thu', this.date_filter.current_week?.days?.thu )
        this.render_week_day( 'fri', this.date_filter.current_week?.days?.fri )
    }

    render_week_day( day: string, date?: DateTime ): void {
        const table: HTMLElement|null = this.get_container().querySelector( '.evaluation-data[data-date="week"]' )

        if ( ! table || ! date ) {
            return
        }

        const map: string[]  = [ 'MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO' ]

        const weekday: string = map[date.weekday - 1]

        const times: GT_Time[] = this.get_times().filter(
            ( time: GT_Time ): boolean => {
                if ( ! time.start_date ) {
                    return false;
                }

                const dt = DateTime.fromSQL(
                    time.start_date,
                )

                return dt.hasSame( date, 'day' )
            }
        )

        const sum: number = times.reduce(
            ( accumulator: number, currentValue: GT_Time ): number => accumulator + currentValue.duration,
            0,
        )

        const value: string = sum > 0 ? `${minutes_to_duration( sum )} Stunden` : '-'

        const row = table.querySelector( `tr[data-day="${day}"]` ) as HTMLElement

        row.innerHTML = `
            <td data-cell="date-badge">
                <span class="badge">${weekday}</span>
            </td>
            <td data-cell="time-start-date">
                <i class="calendar alternate outline icon"></i> 
                ${date.toFormat( 'D' )}
            </td>
            <td data-cell="time-sum" class="right aligned">${value}</td>
        `
    }

    render_month_data() {
        const table: HTMLElement|null = document.querySelector( '.evaluation-data[data-date="month"]' )

        if ( ! table ) {
            return
        }

        const tbody = table.querySelector( 'tbody' ) as HTMLElement
        tbody.innerHTML = ''

        const start = this.date_filter.current_month.start_of_month
        const end = this.date_filter.current_month.end_of_month

        let monday = start.startOf( 'week' )

        console.log( start.toFormat( 'D' ), end.toFormat( 'D' ) )

        while ( monday <= end ) {
            tbody.appendChild( this.render_month_week( monday ) )
            console.log( 'monday', monday.toFormat( 'D' ) )
            monday = monday.plus( { 'weeks': 1 } )
        }
    }

    render_month_week( monday: DateTime ) {
        const tr: HTMLElement = document.createElement( 'tr' )

        const start = monday < this.date_filter.current_month.start_of_month ? this.date_filter.current_month.start_of_month : monday
        const end = monday.endOf( 'week' ) > this.date_filter.current_month.end_of_month ? this.date_filter.current_month.end_of_month : monday.endOf( 'week' )

        const results = this.get_times().filter(
            ( time: GT_Time ): boolean => {
                if ( ! time.start_date ) {
                    return false;
                }

                const dt = DateTime.fromSQL( time.start_date )

                return dt >= start && dt <= end
            }
        )

        const sum = results.reduce(
            ( accumulator: number, currentValue: GT_Time ): number => accumulator + currentValue.duration,
            0,
        )

        const value: string = sum > 0 ? `${minutes_to_duration( sum )} Stunden` : '-'

        tr.innerHTML = `
             <td data-cell="date-badge"><span class="badge">KW ${monday.toFormat( 'WW/kkkk' )}</span></td>
            <td data-cell="time-start-date">
                <i class="calendar alternate outline icon"></i>                
                ${start.toFormat( 'D' )} - ${end.toFormat( 'D' )}
            </td>
            <td class="right aligned" data-cell="time-sum">${value}</td>
        `

        return tr
    }

    render_year_data() {
        const table: HTMLElement|null = document.querySelector( '.evaluation-data[data-date="year"]' )

        if ( ! table ) {
            return
        }

        const tbody = table.querySelector( 'tbody' ) as HTMLElement
        tbody.innerHTML = ''

        tbody.appendChild( this.set_year_month( 'Januar', 1 ) )
        tbody.appendChild( this.set_year_month( 'Februar', 2 ) )
        tbody.appendChild( this.set_year_month( 'März', 3 ) )
        tbody.appendChild( this.set_year_month( 'April', 4 ) )
        tbody.appendChild( this.set_year_month( 'Mai', 5 ) )
        tbody.appendChild( this.set_year_month( 'Juni', 6 ) )
        tbody.appendChild( this.set_year_month( 'Juli', 7 ) )
        tbody.appendChild( this.set_year_month( 'August', 8 ) )
        tbody.appendChild( this.set_year_month( 'September', 9 ) )
        tbody.appendChild( this.set_year_month( 'Oktober', 10 ) )
        tbody.appendChild( this.set_year_month( 'November', 11 ) )
        tbody.appendChild( this.set_year_month( 'Dezember', 12 ) )
    }

    set_year_month( name: string, index: number  ) {
        const tr = document.createElement( 'tr' )

        const results: GT_Time[] = this.get_times().filter(
            ( time: GT_Time ): boolean => {
                if ( ! time.start_date ) {
                    return false;
                }

                const dt = DateTime.fromSQL( time.start_date )

                console.log( 'year', dt.year, 'month', dt.month )

                return dt.year === this.date_filter.current_year.year && dt.month === index
            }
        )

        const start = DateTime.fromObject(
            {
                year: this.date_filter.current_year.year,
                month: index,
            }
        )

        const end = start.endOf( 'month' )

        const sum = results.reduce(
            ( accumulator: number, currentValue: GT_Time ): number => accumulator + currentValue.duration,
            0,
        )

        const value: string = sum > 0 ? `${minutes_to_duration( sum )} Stunden` : '-'

        tr.innerHTML = `
            <td data-cell="date-badge"><span class="badge">${name}</span></td>
            <td data-cell="time-start-date">
                <i class="calendar alternate outline icon"></i>                
                ${start.toFormat( 'D' )} - ${end.toFormat( 'D' )}
            </td>
            <td class="right aligned" data-cell="time-sum">${value}</td>
        `

        return tr
    }

    render_widget_value( name: string, value: string ) {
        const widget = document.querySelector( `[data-evaluation="${name}"]` )

        if ( ! widget ) {
            return
        }

        const content = widget.querySelector( '.content' )!
        content.innerHTML = value
    }

    calculate_sum() {
        return this.get_times().reduce(
            ( accumulator: number, currentValue: GT_Time ): number => accumulator + currentValue.duration,
            0,
        )
    }

    clear_results() {
        console.log( 'clearing' )
        this.clear_week_results()
        this.clear_month_results()
        this.clear_year_results()

        this.get_chart().clear()
    }

    clear_week_results() {
        const rows  = this.get_container().querySelectorAll( '.evaluation-data[data-date="week"] tbody tr' )

        for ( const row of Array.from( rows ) ) {
            row.innerHTML = ''
        }
    }

    clear_month_results() {
        const tbody  = this.get_container().querySelector( '.evaluation-data[data-date="month"] tbody' ) as HTMLElement
        tbody.innerHTML = ''
    }

    clear_year_results() {
        const tbody  = this.get_container().querySelector( '.evaluation-data[data-date="year"] tbody' ) as HTMLElement
        tbody.innerHTML = ''
    }

    change_current_date( e: ClickEvent ) {
        console.log( 'change current date',  e.target )

        this.clear_results()

        const trigger: HTMLButtonElement = e.currentTarget
        const offset: number = trigger.classList.contains( 'next-date' ) ? 1 : -1

        switch ( this.date_mode ) {
            case 'week':
                this.date_filter.change_current_week( offset )
                break

            case 'month':
                this.date_filter.change_current_month( offset )
                break

            case 'year':
                this.date_filter.change_current_year( offset )
                break
        }

        const start = this.date_filter.get_current_start()
        const end  = this.date_filter.get_current_end()

        void this.api_load_times( start, end )
    }

    protected disable_filter_interactions() {
        this.date_filter.disable_filter_interactions()

        const buttons = this.get_container().querySelectorAll( '#list-times-view-buttons button' )
        for ( const button of Array.from( buttons ) ) {
            button.classList.add( 'disabled' )
        }

        jQuery( '#list-times-user-filter' ).addClass( 'disabled' )

        const segments = this.get_container().querySelectorAll( '.ui.segment' )
        for ( const segment of Array.from( segments ) ) {
            segment.classList.add( 'loading' )
        }
    }

    protected enable_filter_interactions() {
        this.date_filter.enable_filter_interactions()

        const buttons = this.get_container().querySelectorAll( '#list-times-view-buttons button' )
        for ( const button of Array.from( buttons ) ) {
            button.classList.remove( 'disabled' )
        }

        jQuery( '#list-times-user-filter' ).removeClass( 'disabled' )

        const segments = this.get_container().querySelectorAll( '.ui.segment' )
        for ( const segment of Array.from( segments ) ) {
            segment.classList.remove( 'loading' )
        }
    }

    /**
     * Lädt die Arbeitszeiten eines Zeitraums.
     *
     * @param start Beginn des Zeitraums.
     * @param end   Ende des Zeitraums.
     */
    async api_load_times( start: DateTime, end: DateTime ) {
        const TASK = 'Laden der Arbeitszeiten'

        if ( ! start.isValid || ! end.isValid ) {
            return
        }

        this.disable_filter_interactions()

        try {
            const response = await this.rest_client.get(
                'goehler-tool/v1/times/load-times',
                {
                    start: start.toFormat( 'yyyy-MM-dd' ),
                    end: end.toFormat( 'yyyy-MM-dd' )
                }
            ) as { times: GT_Time[] }

            gt_results.times = response.times

            this.render_results()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_filter_interactions()
        }
    }
}
